
import {debounceTime} from 'rxjs/operators';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Business } from '../models/business.model';
import { hasTag, getSubTags, loadTags } from '../../util/tag-utils';
import { IConditionalSection } from '../../shared/models/condition-section.model';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { TagService } from '../../shared/services/tag.service';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { MatDialog, MatDialogConfig, MatSnackBar, MatRadioChange, MatCheckboxChange } from '@angular/material';
import { EditTagsComponent } from '../../shared/edit-tags/edit-tags.component';
import { BusinessTag } from '../models/business-tag.model';
import { Tag } from '../../shared/models/tag.model';
import { FlightSchool } from '../models/flight-school.model';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';


@Component({
  selector: 'app-training',
  templateUrl: './training.component.html',
  styleUrls: ['./training.component.scss']
})
export class TrainingComponent implements OnInit, IConditionalSection, OnDestroy {

  //#region Fields
  @Input() business: Business;
  classificationTags: String[] = [];
  servicesTags: String[] = [];
  certificationTags: String[] = [];
  ratingTags: String[] = [];
  advancedTrainingTags: String[] = [];
  hasTag = hasTag;
  getSubTags = getSubTags;
  editModeSet = false;
  allTags: BusinessTag[];
  vaApprovedTag: Tag;
  privateTag: Tag;
  isApprovedForVaTraining: boolean;
  isPrivate: boolean;
  cfi_FullTime: number;
  cfi_PartTime: number;
  cfi_Contracted: number;
  cfi_FullTime_Subscription$: Subscription;
  cfi_PartTime_Subscription$: Subscription;
  cfi_Contracted_Subscription$: Subscription;
  collegeName: string;
  fullTimeInstructorRangeControl = new FormControl('', [Validators.min(0), Validators.max(200)]);
  partTimeInstructorRangeControl = new FormControl('', [Validators.min(0), Validators.max(200)]);
  contractInstructorRangeControl = new FormControl('', [Validators.min(0), Validators.max(200)]);
  //#endregion
  isRegionalManager = false;

  constructor(private _toggleService: EditToggleService,
    private _tagService: TagService,
    private _businessManagerService: BusinessManagerService,
    private _editTrainingDialog: MatDialog,
    public snackBar: MatSnackBar) {
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
  }

  async ngOnInit() {
    this.loadAllBusinessTags();
    await this._tagService.getAllTags().then(allTags => {
      this.setVaApprovedTagAndBool(allTags);
      this.setPrivateTagAndBool(allTags);
    });
    this.setCollegeName();
    this.setFullTimeCfiCount();
    this.setPartTimeCfiCount();
    this.setContractedCfiCount();
    this.setCfi_FullTime_Subscription$();
    this.setCfi_PartTime_Subscription$();
    this.setCfi_Contract_Subscription$();
    this.isRegionalManager = await this._businessManagerService.isRegionalManager()
      && !await this._businessManagerService.userOwnsBusiness(this.business.id);
  }

  ngOnDestroy() {
    // These subscriptions are created near the end of the async ngOnInit (after an awaited tag lookup), so
    // they can still be undefined if the component is destroyed before ngOnInit finishes (e.g. navigating
    // away quickly, as with "Back to Search"). Guard each unsubscribe so ngOnDestroy never throws — an
    // unhandled error here aborts the in-progress router navigation and leaves a blank page.
    if (this.cfi_FullTime_Subscription$) {
      this.cfi_FullTime_Subscription$.unsubscribe();
    }
    if (this.cfi_PartTime_Subscription$) {
      this.cfi_PartTime_Subscription$.unsubscribe();
    }
    if (this.cfi_Contracted_Subscription$) {
      this.cfi_Contracted_Subscription$.unsubscribe();
    }
  }

  get hasContent(): boolean {
    /* uncomment below line if flight instructor counts come back */
    // return this.hasTrainingData || this.hasTrainingOpportunitiesData || this.hasFlightSchoolData;
    return this.hasTrainingData || this.hasTrainingOpportunitiesData;
  }

  get hasTrainingData(): boolean {
    return (this.classificationTags.length > 0 || this.servicesTags.length > 0);
  }

  get hasTrainingOpportunitiesData(): boolean {
    return (this.certificationTags.length > 0 || this.ratingTags.length > 0 ||
      this.advancedTrainingTags.length > 0);
  }

  get hasFlightSchoolData(): boolean {
    return this.business.flightSchool ?
      (this.business.flightSchool.cfiCountFull > 0 ||
        this.business.flightSchool.cfiCountPart > 0 ||
        this.business.flightSchool.cfiCountContract > 0) : false;
  }

  //#region NgInit Methods
  loadAllBusinessTags() {
    this.classificationTags = loadTags(this.business.tags, 'Flight Training::Classification::', '', '');
    this.servicesTags = loadTags(this.business.tags, 'Flight Training::Services::', '', '');
    this.certificationTags = loadTags(this.business.tags, 'Flight Training::Certification::', '', '');
    this.ratingTags = loadTags(this.business.tags, 'Flight Training::Rating::', '', '');
    this.advancedTrainingTags = loadTags(this.business.tags, 'Flight Training::Advanced::', '', '');
  }

  setFullTimeCfiCount() {
    if (this.business.flightSchool
      && (this.business.flightSchool.cfiCountFull
        || this.business.flightSchool.cfiCountFull === 0)) {
      this.cfi_FullTime = this.business.flightSchool.cfiCountFull;
    } else {
      this.cfi_FullTime = null;
    }
  }

  setPartTimeCfiCount() {
    if (this.business.flightSchool
      && (this.business.flightSchool.cfiCountPart
        || this.business.flightSchool.cfiCountPart === 0)) {
      this.cfi_PartTime = this.business.flightSchool.cfiCountPart;
    } else {
      this.cfi_PartTime = null;
    }
  }

  setContractedCfiCount() {
    if (this.business.flightSchool
      && (this.business.flightSchool.cfiCountContract
        || this.business.flightSchool.cfiCountContract === 0)) {
      this.cfi_Contracted = this.business.flightSchool.cfiCountContract;
    } else {
      this.cfi_Contracted = null;
    }
  }

  setCollegeName() {
    if (this.business.collegeName) {
      this.collegeName = this.business.collegeName;
    }
  }

  setVaApprovedTagAndBool(bTags: BusinessTag[]) {
    this.vaApprovedTag = this._tagService.findTagByName(bTags, 'Flight Training::VA Approved');
    this.isApprovedForVaTraining = hasTag(this.business.tags, this.vaApprovedTag.tagName);
  }

  setPrivateTagAndBool(bTags: BusinessTag[]) {
    this.privateTag = this._tagService.findTagByName(bTags, 'Flight Training::Private');
    this.isPrivate = hasTag(this.business.tags, this.privateTag.tagName);
  }

  setCfi_FullTime_Subscription$() {
    this.cfi_FullTime_Subscription$ = this.fullTimeInstructorRangeControl.valueChanges.pipe(debounceTime(1000)).subscribe(
      (newVal) => {
        this.cfi_FullTime = newVal;
        this.updateCfiCount(this.cfi_FullTime);
      },
      (error) => this.snackBar.open(error, 'Error', { duration: 6000 }));
  }

  setCfi_PartTime_Subscription$() {
    this.cfi_PartTime_Subscription$ = this.partTimeInstructorRangeControl.valueChanges.pipe(debounceTime(1000)).subscribe(
      (newVal) => {
        this.cfi_PartTime = newVal;
        this.updateCfiCount(this.cfi_PartTime);
      },
      (error) => this.snackBar.open(error, 'Error', { duration: 6000 }));
  }

  setCfi_Contract_Subscription$() {
    this.cfi_Contracted_Subscription$ = this.contractInstructorRangeControl.valueChanges.pipe(debounceTime(1000)).subscribe(
      (newVal) => {
        this.cfi_Contracted = newVal;
        this.updateCfiCount(this.cfi_Contracted);
      },
      (error) => this.snackBar.open(error, 'Error', { duration: 6000 }));
  }
  //#endregion

  isSectionVisible(sectionTags: [String[]]): boolean {
    return this.editModeSet || sectionTags.some(sectionArray => sectionArray.length > 0);
  }

  openEditTrainingDialog(group: String[], except: String[], title: string): void {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      business: this.business,
      tagsGroup: group,
      except: except,
      title: title
    };

    dialogConfig.width = '95%';
    const dialogRef = this._editTrainingDialog.open(EditTagsComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (isSuccess) {
        this.loadAllBusinessTags();
      } else {
        this.snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  approvedForVaTrainingChange(radioChange: MatRadioChange) {
    if (radioChange.source.value === '1' && !this.isApprovedForVaTraining) {
      this.isApprovedForVaTraining = true;
      this.updateTagStatus(this.vaApprovedTag, false);
    } else if (radioChange.source.value === '2' && this.isApprovedForVaTraining) {
      this.isApprovedForVaTraining = false;
      this.updateTagStatus(this.vaApprovedTag, true);
    }
  }

  isPrivateChange(checkboxChange: MatCheckboxChange) {
    if (checkboxChange.source.checked === true) {
      this.isPrivate = true;
      this.updateTagStatus(this.privateTag, false);
    } else if (checkboxChange.checked === false) {
      this.isPrivate = false;
      this.updateTagStatus(this.privateTag, true);
    }
  }

  async updateTagStatus(tag: Tag, shouldDelete: boolean) {
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    try {
      const tagId = tag.tagId;
      if (shouldDelete) {
        await this._tagService.deleteTag(tagId, this.business.id);
      } else {
        await this._tagService.postTag(tagId, this.business.id);
      }
      await this._businessManagerService.updateBusiness(this.business)
        .then(newBusiness => Object.assign(this.business, newBusiness));
    } catch (e) {
      this.snackBar.open(ERROR_MSG, 'Error', {
        duration: 6000
      });
      throw e;
    }
  }

  async updateCollegeName(name: string) {
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    try {
      const collegeAffiliatedTag = await this._tagService.findTagByName(this.business.tags, 'Flight Training::College Affiliated');
      if (collegeAffiliatedTag != null && (name == null || name.length === 0)) {
        this.updateTagStatus(collegeAffiliatedTag, true);
      } else if (name != null && name.length > 0) {
        const collegeAffiliatedTagId = 181;
        if (collegeAffiliatedTag == null) {
          await this._tagService.postTag(collegeAffiliatedTagId, this.business.id);
        }
      }
      this.business.collegeName = name;
      await this._businessManagerService.updateBusiness(this.business)
        .then(newBusiness => Object.assign(this.business, newBusiness));
      this.snackBar.open('College/University Affiliation updated', 'Success', {
        duration: 6000
      });
    } catch (e) {
      this.snackBar.open(ERROR_MSG, 'Error', {
        duration: 6000
      });
      throw e;
    }
  }

  async updateCfiCount(num: number) {
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';

    if (this.numberInRange(num, 0, 200)) {
      try {
        if (!this.business.flightSchool) {
          this.business.flightSchool = new FlightSchool();
        }
        this.business.flightSchool.cfiCountFull = this.cfi_FullTime;
        this.business.flightSchool.cfiCountPart = this.cfi_PartTime;
        this.business.flightSchool.cfiCountContract = this.cfi_Contracted;
        await this._businessManagerService.updateFlightSchool(this.business)
          .subscribe(newBusiness => Object.assign(this.business, newBusiness));
      } catch (e) {
        this.snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
        throw e;
      }
    }
  }

  //#region Helper Functions
  numberInRange(num: number, min: number, max: number): boolean {
    return (num >= min) && (num <= max);
  }
  //#endregion

}
